'use client'
import FormInput from "@/app/components/Forms/FormInput";
import { useEffect, useState } from "react";
import LinkElement from "@/app/components/General/LinkElement";
import { eButtonColorTheme } from "@/app/models/enum/eButtonColorTheme";
import { eChevronPosition } from "@/app/models/enum/chevronPosition";
import { clientLogError, clientLogInfo } from "@/app/helpers/clientLoggingHelper";
import { iClaimReportModel } from "@/app/models/claimReportModel";
import Loader from "@/app/components/Loader/Loader";
import { iDictionary } from "@/app/models/dictionary";

interface iClaimReportFormProps {
  translations: any;
  country: string;
}

export default function ClaimReportForm({ translations, country }: iClaimReportFormProps) {
  const [formModel, setFormModel] = useState<iClaimReportModel>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showValidationError, setShowValidationError] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<iDictionary>();

  const updateValue = (name: string, value: string) => {
    const newModel = { ...formModel, [name]: value };
    setFormModel(newModel);

    if (isDirty) {
      validateFormModel();
    }
  }

  const validateFormModel = (): boolean => {
    let isValid = true;
    let newInputErrors: iDictionary = {};

    // if (!formModel) {
    //   isValid = false;
    //   return isValid;
    // }

    if (!formModel?.facility) {
      isValid = false;
      newInputErrors = {
        ...newInputErrors, ["facility"]: [`${translations.pages.claimsReportPage.facility} ${translations.pages.claimsReportPage.isMandatory}`]
      };
    }

    if (!formModel?.unitNumber) {
      isValid = false;
      newInputErrors = {
        ...newInputErrors, ["unitNumber"]: [`${translations.pages.claimsReportPage.unitNumber} ${translations.pages.claimsReportPage.isMandatory}`]
      };
    }

    if (!formModel?.forename) {
      isValid = false;
      newInputErrors = {
        ...newInputErrors, ["forename"]: [`${translations.pages.claimsReportPage.forename} ${translations.pages.claimsReportPage.isMandatory}`]
      };
    }

    if (!formModel?.surname) {
      isValid = false;
      newInputErrors = {
        ...newInputErrors, ["surname"]: [`${translations.pages.claimsReportPage.surname} ${translations.pages.claimsReportPage.isMandatory}`]
      };
    }

    if (!formModel?.phone) {
      isValid = false;
      newInputErrors = {
        ...newInputErrors, ["phone"]: [`${translations.pages.claimsReportPage.phone} ${translations.pages.claimsReportPage.isMandatory}`]
      };
    }

    if (!formModel?.mail) {
      isValid = false;
      newInputErrors = {
        ...newInputErrors, ["mail"]: [`${translations.pages.claimsReportPage.mail} ${translations.pages.claimsReportPage.isMandatory}`]
      };
    }

    if (!formModel?.whenDiscovered) {
      isValid = false;
      newInputErrors = {
        ...newInputErrors, ["whenDiscovered"]: [`${translations.pages.claimsReportPage.whenDiscovered} ${translations.pages.claimsReportPage.isMandatory}`]
      };
    }

    if (!formModel?.placeOfDiscovery) {
      isValid = false;
      newInputErrors = {
        ...newInputErrors, ["placeOfDiscovery"]: [`${translations.pages.claimsReportPage.placeOfDiscovery} ${translations.pages.claimsReportPage.isMandatory}`]
      };
    }

    if (!formModel?.reasonOfDamage) {
      isValid = false;
      newInputErrors = {
        ...newInputErrors, ["reasonOfDamage"]: [`${translations.pages.claimsReportPage.reasonOfDamage} ${translations.pages.claimsReportPage.isMandatory}`]
      };
    }

    setValidationErrors(newInputErrors);

    return isValid;
  }

  const submitForm = async () => {
    setIsLoading(true);
    setIsDirty(true);

    if (!validateFormModel()) {
      setIsLoading(false);
      // setShowError(true);
      setShowValidationError(true)
      setShowSuccess(false);
      return;
    } else {
      setShowValidationError(false);
    }

    try {
      clientLogInfo("ClaimReportForm.tsx", "submitForm", "formModel", formModel);
      const response = await fetch("/api/user/sendClaimReport", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Country: country,
        },
        body: JSON.stringify(formModel)
      });

      if (!response.ok) {
        clientLogError("ClaimReportForm.tsx", "submitForm", "response not ok!", await response.text());
        throw new Error("Logout failed");
      }

      const success = await response.json() as boolean;

      if (success) {
        clientLogInfo("ClaimReportForm.tsx", "submitForm", "claim sent successfully");
        setShowSuccess(true);
        setShowError(false);
        setIsDirty(false);
      } else {
        setShowSuccess(false);
        setShowError(true);
      }
    } catch (error) {
      setShowError(true);
      setShowSuccess(false);
      // Handle error
      clientLogError("ClaimReportForm.tsx", "submitForm", error);
    } finally {
      setIsLoading(false);
    }
  }


  return (
    <>
      {isLoading ? (
        <div className="mb-6">
          <Loader
            loading={isLoading}
            useFullWindow={false}
            useNoBackground={true}
          />
        </div>
      ) : (
        <>
          {showValidationError && (
            <div className="mb-10 text-center">
              <h3 className="font-warning">{translations.pages.claimsReportPage.errors.validation.title}</h3>
              <div>{translations.pages.claimsReportPage.errors.validation.message}</div>
            </div>
          )}
          {showError && (
            <div className="mb-10 text-center">
              <h3 className="font-warning">{translations.pages.claimsReportPage.errors.error.title}</h3>
              <div>{translations.pages.claimsReportPage.errors.error.message}</div>
            </div>
          )}
          {showSuccess ? (
            <div className="mb-10 text-center">
              <h3>{translations.pages.claimsReportPage.successMessage}</h3>
            </div>
          ) : (<>
            <div className="grid grid-cols-2 mb-10 gap-4 w-full lg:w-2/3">
              <FormInput
                name="facility"
                label={translations.pages.claimsReportPage.facility}
                placeholder={translations.pages.claimsReportPage.facility}
                type="text"
                isMandatory={true}
                onValueChange={(name, value) => updateValue(name, value)}
                errors={validationErrors}
                value={formModel?.facility ?? ""}
              />

              <FormInput
                name="unitNumber"
                label={translations.pages.claimsReportPage.unitNumber}
                placeholder={translations.pages.claimsReportPage.unitNumber}
                type="text"
                isMandatory={true}
                onValueChange={(name, value) => updateValue(name, value)}
                errors={validationErrors}
                value={formModel?.unitNumber ?? ""}
              />

              <FormInput
                name="forename"
                label={translations.pages.claimsReportPage.forename}
                placeholder={translations.pages.claimsReportPage.forename}
                type="text"
                isMandatory={true}
                onValueChange={(name, value) => updateValue(name, value)}
                errors={validationErrors}
                value={formModel?.forename ?? ""}
              />

              <FormInput
                name="surname"
                label={translations.pages.claimsReportPage.surname}
                placeholder={translations.pages.claimsReportPage.surname}
                type="text"
                isMandatory={true}
                onValueChange={(name, value) => updateValue(name, value)}
                errors={validationErrors}
                value={formModel?.surname ?? ""}
              />

              <FormInput
                name="phone"
                label={translations.pages.claimsReportPage.phone}
                placeholder={translations.pages.claimsReportPage.phone}
                type="text"
                isMandatory={true}
                onValueChange={(name, value) => updateValue(name, value)}
                errors={validationErrors}
                value={formModel?.phone ?? ""}
              />

              <FormInput
                name="mail"
                label={translations.pages.claimsReportPage.mail}
                placeholder={translations.pages.claimsReportPage.mail}
                type="text"
                isMandatory={true}
                onValueChange={(name, value) => updateValue(name, value)}
                errors={validationErrors}
                value={formModel?.mail ?? ""}
              />

              <FormInput
                name="whenDiscovered"
                label={translations.pages.claimsReportPage.whenDiscovered}
                placeholder={translations.pages.claimsReportPage.whenDiscoveredPlaceholder}
                type="text"
                isMandatory={true}
                className="col-span-2 "
                onValueChange={(name, value) => updateValue(name, value)}
                errors={validationErrors}
                value={formModel?.whenDiscovered ?? ""}
              />

              <FormInput
                name="placeOfDiscovery"
                label={translations.pages.claimsReportPage.placeOfDiscovery}
                placeholder={translations.pages.claimsReportPage.placeOfDiscovery}
                type="textarea"
                isMandatory={true}
                className="col-span-2"
                innerClassName="h-[200px]"
                onValueChange={(name, value) => updateValue(name, value)}
                errors={validationErrors}
                value={formModel?.placeOfDiscovery ?? ""}
              />

              <FormInput
                name="reasonOfDamage"
                label={translations.pages.claimsReportPage.reasonOfDamage}
                placeholder={translations.pages.claimsReportPage.reasonOfDamage}
                type="textarea"
                isMandatory={true}
                className="col-span-2"
                innerClassName="h-[200px]"
                onValueChange={(name, value) => updateValue(name, value)}
                errors={validationErrors}
                value={formModel?.reasonOfDamage ?? ""}
              />

              {showValidationError && (
                <div className="mb-10 text-center col-span-2 ">
                  <h3 className="font-warning">{translations.pages.claimsReportPage.errors.validation.title}</h3>
                  <div>{translations.pages.claimsReportPage.errors.validation.message}</div>
                </div>
              )}
              {showError && (
                <div className="mb-10 text-center col-span-2">
                  <h3 className="font-warning">{translations.pages.claimsReportPage.errors.error.title}</h3>
                  <div>{translations.pages.claimsReportPage.errors.error.message}</div>
                </div>
              )}

              <LinkElement
                title="skicka"
                isButton={true}
                buttonColorTheme={eButtonColorTheme.Green}
                onClick={submitForm}
                chevronPosition={eChevronPosition.None}
                className="col-span-2"
                disabled={validationErrors && Object.keys(validationErrors).length > 0}
              >
                {translations.pages.claimsReportPage.sendClaimsReport}
              </LinkElement>
            </div >
          </>)
          }
        </>
      )}
    </>
  )
}